/**
 * chat.ts — room chat and the event log, bottom left.
 *
 * One panel for both, because they are the same thing to a player: a running
 * account of what is happening in the room. The log used to live in the corner
 * with the roster, which meant "Alice joined" and "Alice: hello" appeared in two
 * different places on screen.
 *
 * Y opens the input. While it is open the game must not read the keyboard at all
 * — `onTypingChange` exists for exactly that, and the caller wires it to
 * `input.setTyping`. Without it, typing "was" walks you forward and swings.
 *
 * Pointer lock is deliberately NOT released while typing. Dropping it would show
 * the cursor and let the player click the field, but re-acquiring it afterwards
 * needs a fresh user gesture and browsers rate-limit the request, so a player who
 * sent one message could be left unable to turn. Nothing here needs the mouse:
 * Y opens, Enter sends, Escape cancels.
 *
 * Every line is written with `textContent`. Names and chat text are untrusted, and
 * the server's sanitizer deliberately does not escape markup, so this is the half
 * of that pairing that makes it safe.
 */

import { CHAT_MAX, type ChatMsg } from '@dueler/shared';
import { isTextEntryTarget } from '../debug.js';

/** Lines kept in the panel. More than fits, so there is something to scroll back to. */
const HISTORY = 60;

/** The key that opens the input. */
const OPEN_KEY = 'KeyY';

export interface Chat {
  /** A system line: joins, leaves, round results, control toggles. */
  system(line: string): void;
  /** A line somebody said. `self` is whether it was this player. */
  said(msg: ChatMsg, self: boolean): void;
  /** Whether the input is open, and so whether the game should ignore keys. */
  readonly isOpen: boolean;
  dispose(): void;
}

export interface ChatConfig {
  /** A line the player submitted. Already non-empty; the server sanitizes it. */
  onSay(text: string): void;
  /**
   * The input opened or closed. Wire this to `input.setTyping` — it is what stops
   * the keyboard driving the character while the player is typing.
   */
  onTypingChange(typing: boolean): void;
}

export function createChat(config: ChatConfig): Chat {
  const root = document.createElement('div');
  root.className = 'chat';
  root.innerHTML = `
    <div class="chat-log" id="log"></div>
    <div class="chat-entry" id="entry">
      <span class="chat-hint" id="hint">press <b>Y</b> to chat</span>
      <label class="chat-field" id="field" hidden>
        <span class="chat-prompt">Say:</span>
        <input id="input" maxlength="${CHAT_MAX}" autocomplete="off" spellcheck="false" />
        <span class="chat-keys"><b>Enter</b> to send</span>
      </label>
    </div>
  `;
  document.body.appendChild(root);

  const logEl = root.querySelector<HTMLDivElement>('#log')!;
  const hintEl = root.querySelector<HTMLSpanElement>('#hint')!;
  const fieldEl = root.querySelector<HTMLLabelElement>('#field')!;
  const inputEl = root.querySelector<HTMLInputElement>('#input')!;

  let open = false;

  /** Appends a row and keeps the newest visible. */
  function push(kind: string, parts: { text: string; className?: string }[]): void {
    const row = document.createElement('div');
    row.className = `chat-line ${kind}`;
    for (const part of parts) {
      const span = document.createElement('span');
      if (part.className !== undefined) span.className = part.className;
      // textContent: names and chat text are untrusted.
      span.textContent = part.text;
      row.appendChild(span);
    }
    logEl.appendChild(row);
    while (logEl.childElementCount > HISTORY) logEl.firstElementChild?.remove();
    // Pointer lock means there is no cursor to scroll with, so the newest line
    // has to bring itself into view.
    logEl.scrollTop = logEl.scrollHeight;
  }

  function setOpen(next: boolean): void {
    if (next === open) return;
    open = next;
    fieldEl.hidden = !next;
    hintEl.hidden = next;
    root.classList.toggle('chat-open', next);
    if (next) {
      inputEl.focus();
    } else {
      inputEl.value = '';
      inputEl.blur();
    }
    // Last, so the caller sees the final state whichever way it went.
    config.onTypingChange(next);
  }

  const onWindowKeyDown = (ev: KeyboardEvent): void => {
    if (open || ev.repeat || ev.code !== OPEN_KEY) return;
    // Some other field has focus — the landing form, say. Not ours to hijack.
    if (isTextEntryTarget(ev.target)) return;
    // Without this the keypress that opened the field also types a "y" into it.
    ev.preventDefault();
    setOpen(true);
  };

  const onInputKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      // Escape also belongs to the browser, which uses it to leave pointer lock,
      // and that is not something `preventDefault` can stop. So cancelling with
      // Escape drops mouselook and the player has to click to take it back.
      // Enter is the clean way out, which is why the hint names it — this is kept
      // as the escape hatch people reach for by reflex, not the intended path.
      ev.preventDefault();
      setOpen(false);
      return;
    }
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    const text = inputEl.value.trim();
    // Close either way: Enter on an empty line is how you back out without
    // reaching for Escape.
    setOpen(false);
    if (text.length > 0) config.onSay(text);
  };

  // If focus leaves the field — clicking away, or the window losing it — the
  // player is no longer typing, and leaving `typing` set would lock them out of
  // their own character with no visible cause.
  const onInputBlur = (): void => setOpen(false);

  window.addEventListener('keydown', onWindowKeyDown);
  inputEl.addEventListener('keydown', onInputKeyDown);
  inputEl.addEventListener('blur', onInputBlur);

  return {
    system(line) {
      // Kept as a console line too: the log is a debug affordance first.
      console.log(`[room] ${line}`);
      push('chat-system', [{ text: line }]);
    },
    said(msg, self) {
      push(self ? 'chat-said chat-self' : 'chat-said', [
        { text: `${msg.name}: `, className: 'chat-who' },
        { text: msg.text, className: 'chat-text' },
      ]);
    },
    get isOpen() {
      return open;
    },
    dispose() {
      window.removeEventListener('keydown', onWindowKeyDown);
      inputEl.removeEventListener('keydown', onInputKeyDown);
      inputEl.removeEventListener('blur', onInputBlur);
      // If chat is torn down mid-typing, the caller must not be left suppressed.
      if (open) config.onTypingChange(false);
      root.remove();
    },
  };
}
